/**
 * Phase 3J.7B · Material Traceability Report
 * Read-only cross-document, cross-process material genealogy.
 * NO edit / post / repair / migrate actions.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { MagnifyingGlassIcon, TreeStructureIcon, ClockCounterClockwiseIcon, CheckCircleIcon, XCircleIcon } from "@phosphor-icons/react";

const inp = "h-8 px-2 bg-[var(--bg-2)] border border-[var(--border)] rounded-sm text-xs font-mono focus:outline-none focus:border-[var(--accent)]";
const btn = "h-8 px-3 bg-[var(--accent)] text-black text-[11px] font-mono uppercase tracking-widest rounded-sm flex items-center gap-1.5";

const fmt = (v) => v == null ? "—" : (typeof v === "number" ? v.toLocaleString("en-IN", { maximumFractionDigits: 4 }) : String(v));

export default function MaterialTraceability() {
  const [kind, setKind] = useState("RM");
  const [pickerId, setPickerId] = useState("");
  const [options, setOptions] = useState([]);
  const [optLoading, setOptLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [tab, setTab] = useState("history");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      setOptLoading(true); setPickerId(""); setData(null);
      try {
        let list = [];
        if (kind === "RM") {
          const { data } = await api.get("/raw-materials/");
          list = (data || []).map(r => ({ id: r.id, code: r.code, name: r.name }));
        } else if (kind === "SFG") {
          const { data } = await api.get("/sf-master/");
          list = (data || []).map(r => ({ id: r.id, code: r.sf_code || r.sf_id, name: r.description || r.process }));
        } else {
          const { data } = await api.get("/articles/");
          const rows = Array.isArray(data) ? data : (data.rows || []);
          list = rows.map(r => ({ id: r.id, code: r.product_code || r.article_number || r.code, name: r.name || r.description }));
        }
        setOptions(list.filter(o => o.code));
      } catch (e) {
        toast.error(e?.response?.data?.detail || `Failed to load ${kind} master`);
      } finally { setOptLoading(false); }
    })();
  }, [kind]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options.slice(0, 500);
    return options.filter(o =>
      (o.code || "").toLowerCase().includes(q) ||
      (o.name || "").toLowerCase().includes(q)
    ).slice(0, 500);
  }, [search, options]);

  const run = async () => {
    if (!pickerId) { toast.error(`Pick a ${kind} from the dropdown`); return; }
    setLoading(true); setData(null);
    try {
      const params = { kind, id: pickerId };
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const { data } = await api.get("/reports/material-traceability", { params });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Trace failed");
    } finally { setLoading(false); }
  };

  const exportCsv = () => {
    if (!data) return;
    const wh = (m) => m.warehouse_code
      ? `${m.warehouse_code}${m.warehouse_name ? ` · ${m.warehouse_name}` : ""}`
      : (m.warehouse_name || m.warehouse_id || m.godown_id || "");
    const rows = [
      ["Date","Type","Direction","Qty","Warehouse","Batch","Doc Type","Doc No","Source"],
      ...data.movements.v2.map(m => [m.date, m.type, m.direction, m.qty, wh(m), m.batch_lot, m.source_doc_type, m.source_doc_no, m.source]),
      ...data.movements.legacy.map(m => [m.date, m.type, "", m.qty, wh(m), m.batch_no, "", m.wo_cwo_ref, m.source]),
    ];
    const csv = rows.map(r => r.map(x => `"${(x||"").toString().replaceAll('"','""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = `traceability-${(data.material.code || "")}.csv`;
    a.click();
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-baseline gap-3">
        <h1 className="text-lg font-mono uppercase tracking-widest">Material Traceability</h1>
        <span className="text-[10px] text-zinc-500 font-mono uppercase tracking-widest">Read-only · Genealogy · Flow Integrity</span>
      </div>

      <div className="tactical-card p-3 flex items-end gap-2 flex-wrap">
        <div>
          <label className="overline block mb-1">Material Type</label>
          <div className="flex gap-1">
            {["RM", "SFG", "FG"].map(k => (
              <button key={k} data-testid={`mt-kind-${k.toLowerCase()}`} onClick={() => setKind(k)}
                className={`h-8 px-3 text-[11px] font-mono uppercase tracking-widest rounded-sm ${kind === k ? "bg-[var(--accent)] text-black" : "border border-[var(--border)] text-zinc-300"}`}>
                {k}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 min-w-[400px]">
          <label className="overline block mb-1">Pick from {kind} Master {optLoading && <span className="text-zinc-500">· loading…</span>} <span className="text-zinc-500">· {filtered.length} of {options.length}</span></label>
          <div className="flex gap-2">
            <input data-testid="mt-search-box" value={search} onChange={e => setSearch(e.target.value)}
                   placeholder={`Search ${kind} code or name…`} className={inp + " w-72"} />
            <select data-testid="mt-picker" value={pickerId} onChange={e => setPickerId(e.target.value)}
                    className={inp + " flex-1"}>
              <option value="">— select {kind} —</option>
              {filtered.map(o => (
                <option key={o.id} value={o.id}>{o.code}{o.name ? ` · ${o.name.slice(0,40)}` : ""}</option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label className="overline block mb-1">From</label>
          <input data-testid="mt-from" type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className={inp} />
        </div>
        <div>
          <label className="overline block mb-1">To</label>
          <input data-testid="mt-to" type="date" value={toDate} onChange={e => setToDate(e.target.value)} className={inp} />
        </div>
        <button data-testid="mt-trace" onClick={run} disabled={loading || !pickerId} className={btn}>
          <MagnifyingGlassIcon size={12} weight="bold" />{loading ? "Tracing…" : "Trace"}
        </button>
        {data && (
          <button data-testid="mt-export" onClick={exportCsv} className={btn.replace("bg-[var(--accent)] text-black", "border border-[var(--border)] text-zinc-200 bg-transparent")}>Export CSV</button>
        )}
      </div>

      {data && (
        <>
          <div className="tactical-card p-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs font-mono">
            <div><div className="overline">Material</div><div className="text-zinc-100">{data.material.code}</div></div>
            <div><div className="overline">Type</div><div className="text-purple-300">{data.material.kind}</div></div>
            <div><div className="overline">Name</div><div>{data.material.name || "—"}</div></div>
            <div><div className="overline">UOM</div><div>{data.material.uom || "—"}</div></div>
            <div><div className="overline">Received (V2)</div><div>{fmt(data.totals.received_v2)}</div></div>
            <div><div className="overline">Issued (V2)</div><div>{fmt(data.totals.issued_v2)}</div></div>
            <div><div className="overline">Consumed (V2)</div><div>{fmt(data.totals.consumed_v2)}</div></div>
            <div><div className="overline">Wastage (V2)</div><div>{fmt(data.totals.wastage_v2)}</div></div>
          </div>

          <div className="tactical-card p-3">
            <div className="overline mb-2">Flow Integrity</div>
            <div className="space-y-1 text-xs font-mono">
              {data.flow_integrity.map((f, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`mt-flow-${i}`}>
                  {f.status === "✓" ? <CheckCircleIcon size={13} weight="bold" className="text-emerald-400" />
                   : f.status === "✕" ? <XCircleIcon size={13} weight="bold" className="text-red-400" />
                   : <span className="w-[13px] text-zinc-500">—</span>}
                  <span className="w-64">{f.stage}</span>
                  <span className="text-zinc-400">{f.count != null ? `${f.count} docs` : f.qty != null ? fmt(f.qty) : ""}</span>
                </div>
              ))}
            </div>
            {data.flow_breaks.length > 0 && (
              <div className="mt-3 space-y-1">
                <div className="overline text-red-400">Flow Breaks</div>
                {data.flow_breaks.map((b, i) => (
                  <div key={i} className="text-[11px] text-red-300 font-mono" data-testid={`mt-break-${i}`}>
                    <span className="font-bold">{b.code}</span> · {b.detail}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <button data-testid="mt-tab-history" onClick={() => setTab("history")} className={`h-8 px-3 text-[11px] font-mono uppercase tracking-widest rounded-sm ${tab === "history" ? "bg-[var(--accent)] text-black" : "border border-[var(--border)] text-zinc-300"}`}>
              <ClockCounterClockwiseIcon size={12} weight="bold" className="inline mr-1" />Movement History
            </button>
            <button data-testid="mt-tab-genealogy" onClick={() => setTab("genealogy")} className={`h-8 px-3 text-[11px] font-mono uppercase tracking-widest rounded-sm ${tab === "genealogy" ? "bg-[var(--accent)] text-black" : "border border-[var(--border)] text-zinc-300"}`}>
              <TreeStructureIcon size={12} weight="bold" className="inline mr-1" />Genealogy
            </button>
          </div>

          {tab === "history" && (
            <div className="tactical-card overflow-x-auto">
              <table className="w-full text-xs font-mono">
                <thead className="bg-[var(--bg-2)]">
                  <tr className="text-left overline">
                    <th className="p-2">Date</th><th className="p-2">Type</th><th className="p-2">Dir</th>
                    <th className="p-2 text-right">Qty</th><th className="p-2">Warehouse</th><th className="p-2">Batch</th>
                    <th className="p-2">Doc</th><th className="p-2">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {[...data.movements.v2, ...data.movements.legacy].sort((a,b) => (a.date||"").localeCompare(b.date||"")).map((m, i) => {
                    const whLabel = m.warehouse_code
                      ? `${m.warehouse_code}${m.warehouse_name ? ` · ${m.warehouse_name}` : ""}`
                      : (m.warehouse_name || m.warehouse_id || m.godown_id || "—");
                    const docLabel = m.source_doc_no || m.wo_cwo_ref;
                    return (
                    <tr key={i} className="border-t border-[var(--border)]" data-testid={`mt-mov-${i}`}>
                      <td className="p-2">{(m.date || m.when || "").slice(0, 10)}</td>
                      <td className="p-2">{m.type}</td>
                      <td className={`p-2 ${m.direction === "IN" ? "text-emerald-400" : m.direction === "OUT" ? "text-red-300" : ""}`}>{m.direction || "—"}</td>
                      <td className="p-2 text-right">{fmt(m.qty)}</td>
                      <td className="p-2" data-testid={`mt-mov-wh-${i}`} title={m.warehouse_id || m.godown_id || ""}>{whLabel}</td>
                      <td className="p-2">{m.batch_lot || m.batch_no || "—"}</td>
                      <td className="p-2" data-testid={`mt-mov-doc-${i}`}>
                        {docLabel ? (
                          <div className="flex flex-col leading-tight">
                            <span className="text-zinc-100">{docLabel}</span>
                            {m.source_doc_type && (
                              <span className="text-[9px] text-zinc-500 uppercase tracking-wider">{m.source_doc_type}</span>
                            )}
                          </div>
                        ) : "—"}
                      </td>
                      <td className="p-2"><span className={`px-1.5 py-0.5 text-[9px] rounded-sm ${m.source === "V2" ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/50" : "bg-amber-500/10 text-amber-300 border border-amber-500/50"}`}>{m.source}</span></td>
                    </tr>
                  );})}
                  {data.movements.v2.length + data.movements.legacy.length === 0 && (
                    <tr><td colSpan={8} className="p-4 text-center text-zinc-500">No movements found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {tab === "genealogy" && (
            <div className="tactical-card p-3 space-y-2 text-xs font-mono">
              <div className="overline">Downstream Conversions</div>
              {data.genealogy.length === 0 ? (
                <div className="text-zinc-500">No production conversions consumed this material yet.</div>
              ) : data.genealogy.map((g, i) => (
                <div key={i} className="p-2 border border-[var(--border)] rounded-sm" data-testid={`mt-gen-${i}`}>
                  <div>{data.material.code} <span className="text-zinc-500">→</span> <span className="text-purple-300">{g.process_stage}</span> <span className="text-zinc-500">→</span> <span className="text-emerald-300">{g.item_code}</span></div>
                  <div className="text-[10px] text-zinc-400 mt-1">
                    qty={fmt(g.quantity)} · warehouse={g.warehouse_id} · batch={g.batch_lot} · group={(g.conversion_group_id || "").slice(0,8)}…
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
